import React from "react";
import { SubSection, SubTitle, Paragraph, List, Link } from "./ContributingUI";

const ApplicationInfo = () => {
    return (
        <>
            <SubSection id="where-to-begin">
                <SubTitle>Where to Begin</SubTitle>
                <Paragraph>
                    Contributors are expected to have worked through the{" "}
                    <Link
                        href="https://nest.nitk.ac.in/docs/v0.4/user/tutorial.html"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        NeST tutorial
                    </Link>{" "}
                    and to have read through and executed the code in their
                    areas of interest.
                </Paragraph>
                <Paragraph>
                    The project ideas we list are starting points for
                    contributors to develop further, with help from mentors. We
                    are also open to ideas not found on the list - if you are
                    excited to work on something specific, let us know and we
                    will give you feedback on it.
                </Paragraph>
                <Paragraph>
                    The best applications come from contributors who have a
                    desire to accomplish specific goals (usually aligned with
                    their own research or interests), who can articulate an
                    understanding of the issues involved, who engage with
                    mentors during the application process, and who provide
                    evidence that they have a reasonable plan and the coding
                    skills to make progress.
                </Paragraph>
                <Paragraph>
                    You are more likely to get help if you ask specific
                    questions that demonstrate you have tried to make progress.
                    A generic question like &quot;I am interested in NeST; can
                    you tell me how to begin?&quot; is unlikely to generate
                    helpful guidance. Instead, try something like: &quot;I have
                    read the code in the WiFi module and run the examples. It
                    seems to me that feature X is not yet supported. Can you
                    give me feedback about which features might be the most
                    useful to prioritize?&quot;
                </Paragraph>
            </SubSection>

            <SubSection id="how-to-apply">
                <SubTitle>How to Apply</SubTitle>
                <Paragraph>
                    Contributors apply directly to Google. For more information
                    on how to apply, please look at the{" "}
                    <Link
                        href="https://summerofcode.withgoogle.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Google Summer of Code website
                    </Link>
                    .
                </Paragraph>
            </SubSection>

            <SubSection id="benefits">
                <SubTitle>Contributor Benefits</SubTitle>
                <List>
                    <li>
                        GSoC is an excellent opportunity to gain experience
                        working on an open source project.
                    </li>
                    <li>
                        Working with NeST will allow you to improve your
                        networking knowledge, Python skills, and be in contact
                        with a skilled team of developers.
                    </li>
                    <li>
                        NeST is used in academic institutions and IETF
                        hackathons for testing protocols like MPTCP, BBR, and
                        queue management algorithms - you will be contributing
                        to real-world research tools.
                    </li>
                </List>
            </SubSection>

            <SubSection id="advice">
                <SubTitle>A Piece of Advice</SubTitle>
                <Paragraph>
                    The most important factor in the success of an application
                    and project is <strong>communication</strong>. That process
                    begins in the application phase. Without joining Zulip and
                    initiating a discussion of your ideas, it is unlikely that
                    your application will be complete or rich enough to be
                    competitive.
                </Paragraph>
                <Paragraph>
                    During the program, every contributor is expected to
                    communicate regularly with their mentor, post weekly updates
                    on Zulip, and participate in discussions about their work.
                </Paragraph>
            </SubSection>
        </>
    );
};

export default ApplicationInfo;
